import { Link } from 'react-router-dom';
import type { FlightOfferDto } from '@wingseat/shared';
import { formatDateRange, formatRoute } from '../lib/format';

const STATUS_LABELS: Record<FlightOfferDto['status'], string> = {
  ACTIVE: 'aktiv',
  FULL: 'voll',
  CANCELLED_BY_PILOT: 'storniert',
  CANCELLED_BY_ADMIN: 'admin-deakt.',
  EXPIRED: 'abgelaufen',
};

const STATUS_STYLES: Record<FlightOfferDto['status'], string> = {
  ACTIVE: 'bg-emerald-900/40 text-emerald-300 border-emerald-800',
  FULL: 'bg-amber-900/40 text-amber-300 border-amber-800',
  CANCELLED_BY_PILOT: 'bg-rose-900/40 text-rose-300 border-rose-800',
  CANCELLED_BY_ADMIN: 'bg-rose-900/40 text-rose-300 border-rose-800',
  EXPIRED: 'bg-slate-800 text-slate-400 border-slate-700',
};

const BOOKING_BADGE: Record<string, { text: string; cls: string }> = {
  REQUESTED: {
    text: 'angefragt',
    cls: 'bg-blue-900/40 text-blue-300 border-blue-800',
  },
  WAITLISTED: {
    text: 'Warteliste',
    cls: 'bg-amber-900/40 text-amber-300 border-amber-800',
  },
  CONFIRMED: {
    text: 'bestätigt',
    cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-800',
  },
};

export function FlightCard({ offer }: { offer: FlightOfferDto }) {
  const reg =
    offer.aircraft?.registration ??
    offer.aircraftRegistrationText ??
    'unbekannt';
  const typeName =
    offer.aircraft?.type?.name ?? offer.aircraftType?.name ?? null;
  const seatsLeft = offer.freeSeats - offer.confirmedSeats;
  const my = offer.myBookingRequest;
  const myBadge =
    my && (my.status === 'REQUESTED' || my.status === 'WAITLISTED' || my.status === 'CONFIRMED')
      ? BOOKING_BADGE[my.status]
      : null;

  return (
    <Link
      to={`/app/flights/${offer.id}`}
      className="block rounded-xl border border-slate-800 bg-slate-900/60 p-4 active:scale-[0.99] transition-transform"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-slate-500">
            {formatDateRange(offer.startAt, offer.endAt)}
          </p>
          <p className="mt-1 text-base font-semibold truncate">
            {formatRoute(offer.fromLocation, offer.toLocation, offer.roundTrip)}
          </p>
          <p className="mt-1 text-xs text-slate-400 truncate">
            {reg}
            {typeName ? ` · ${typeName}` : ''}
            {offer.pilot ? ` · ${offer.pilot.firstName} ${offer.pilot.lastName}` : ''}
          </p>
          {offer.event && (
            <p className="mt-1 inline-flex items-center gap-1 rounded-full border border-brand-800 bg-brand-900/30 px-2 py-0.5 text-[10px] font-medium text-brand-200">
              📅 {offer.event.name}
            </p>
          )}
        </div>
        <span
          className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${STATUS_STYLES[offer.status]}`}
        >
          {STATUS_LABELS[offer.status]}
        </span>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <span className="text-slate-400">
            {seatsLeft > 0 ? `${seatsLeft} frei` : 'belegt'} · {offer.freeSeats} gesamt
          </span>
          {offer.sharedPic && (
            <span className="rounded-full border border-brand-800 bg-brand-900/30 px-1.5 py-0.5 text-[9px] font-medium text-brand-200">
              Shared PiC
            </span>
          )}
          {offer.costShareText && (
            <span className="text-slate-500">· {offer.costShareText}</span>
          )}
        </div>
        {myBadge && (
          <span
            className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${myBadge.cls}`}
          >
            {myBadge.text}
          </span>
        )}
      </div>
    </Link>
  );
}
